/**
 * Contact Page — Route Module
 *
 * SEO thin-content pass. `/contact` is `indexable: true` in the routes SSOT
 * and rendered 331 characters of editorial text in en, 129 in zh-HK, 132 in
 * zh-CN: a heading, a one-line blurb, a sign-in prompt and a button. It told a
 * visitor nothing about what support actually covers, what to put in a
 * message, or that an in-app conversation is the ONLY channel — which is the
 * single most useful fact this page has to give, and the one it was hiding.
 *
 * ⚠ The measure is CHARACTERS PER LOCALE, never words. Ahrefs' "low word
 * count" flag whitespace-tokenizes, which scores Chinese at roughly a quarter:
 * all 25 pages it flagged on this site are zh-HK or zh-CN and none is `/en/`,
 * while `/forms` scores 49 "words" on 2,335 characters. A word-count target is
 * permanently red on Chinese and permanently green on an empty English page.
 *
 * THE TOPIC LIST IS DERIVED. It is `OFFERED_SUPPORT_CATEGORIES` — the same
 * array `/account/support` renders as its picker, whose slugs are checked
 * against `support.v1.SupportCaseCategory` — so this page cannot describe a
 * topic the picker does not offer, or miss one it does. The per-topic sentence
 * is keyed by a `Record<SupportCaseCategorySlug, …>`, which makes the mapping
 * EXHAUSTIVE: a ninth proto variant fails to compile here until someone writes
 * what it is for.
 */
import { routes } from '@letsgethome/routes';
import { Button } from '@letsgethome/ui/button';
import { PageContainer } from '@letsgethome/ui/page-container';

import { SeoHead } from '../../../../shared/components/SeoHead';
import { useStaticSeo } from '../../../../shared/hooks/seo/use-static-seo';
import { EnhancedLink, Link } from '../../../../shared/i18n/routing';
import { useTranslations } from '../../../../shared/i18n/useTranslations';
import { OFFERED_SUPPORT_CATEGORIES } from '../../../../shared/lib/support-categories';
import type { SupportCaseCategorySlug } from '../../../../shared/lib/types/conversations';
import { useAuthGate } from '../../../../shared/providers/AuthProvider';

/**
 * What each support topic is for, keyed by the proto-derived category slug.
 *
 * `Record<SupportCaseCategorySlug, …>` rather than an array: the compiler
 * requires an arm for every category the picker can offer, so the prose and
 * the picker cannot drift apart in either direction. The keys stay literal for
 * the static i18n-key coverage gate.
 */
const TOPIC_BLURB_KEY = {
  general: 'topics.general',
  billing: 'topics.billing',
  technical: 'topics.technical',
  account: 'topics.account',
  listing: 'topics.listing',
  inquiry: 'topics.inquiry',
  complaint: 'topics.complaint',
  other: 'topics.other',
} as const satisfies Record<SupportCaseCategorySlug, string>;

/** What to put in the first message — the four things that save a round trip. */
const INCLUDE_KEYS = [
  'include.listing',
  'include.expected',
  'include.when',
  'include.device',
] as const;

export function Component() {
  const t = useTranslations('contact');
  const tSupport = useTranslations('support');
  const { requireAuth } = useAuthGate();

  // "Log in and chat" is a REAL ANCHOR to /account/support, auth-gated through
  // the routing seam (LET-83): with JS a signed-out click opens the modal in
  // place; without it the browser navigates and the auth middleware redirects
  // to the sign-in modal. It used to be a `Button onClick` whose destination
  // existed only in JS — "a button, not a link, so crawlers do not follow an
  // auth-gated flow". That intent is now stated on the link itself
  // (`rel="nofollow"`; robots.txt Disallows `/account` regardless) instead of
  // by withholding the URL from every reader. Messaging PR-H1: /account/support
  // is a REAL route again — the support entry (category picker + optional
  // first message → EnsureSupportCase → the case's thread). The old target,
  // the conversations INDEX, never created a thread: a user with no
  // conversations landed on an empty inbox with nobody to talk to.

  return (
    <>
      <SeoHead
        {...useStaticSeo('/contact', { title: t('seo.title'), description: t('seo.description') })}
      />
      <div className="min-h-screen bg-background">
        <PageContainer as="section" size="2xl" className="py-12 md:py-16">
          <div className="space-y-6">
            <h1 className="text-2xl font-bold md:text-3xl">{t('title')}</h1>

            <p className="text-muted-foreground">{t('blurb')}</p>

            <div className="flex items-center gap-2 text-muted-foreground">
              <span aria-hidden>🕐</span>
              <span>{t('typicalResponse')}</span>
            </div>

            <hr className="border-border" />

            <p className="text-muted-foreground">{t('loginToContact')}</p>

            <Button size="sm" className="rounded-lg" asChild>
              <EnhancedLink href={routes.account.support()} gate={requireAuth} rel="nofollow">
                {t('logInAndChat')}
              </EnhancedLink>
            </Button>

            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <span aria-hidden>🔒</span>
              <span>{t('privacyHint')}</span>
            </div>
          </div>

          {/* The one channel. Stated plainly because its absence is what a
              visitor is actually looking for on a contact page: there is no
              hotline and no public inbox, and hunting for one is wasted time. */}
          <section className="mt-14">
            <h2 className="text-xl font-semibold">{t('channel.title')}</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{t('channel.body')}</p>
            <p className="mt-3 leading-relaxed text-muted-foreground">{t('channel.thread')}</p>
          </section>

          {/* Derived from OFFERED_SUPPORT_CATEGORIES — see the module header. */}
          <section className="mt-14">
            <h2 className="text-xl font-semibold">{t('topics.title')}</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{t('topics.intro')}</p>
            <dl className="mt-5 space-y-4">
              {OFFERED_SUPPORT_CATEGORIES.map((category) => (
                <div key={category.slug}>
                  <dt className="font-medium text-foreground">{tSupport(category.labelKey)}</dt>
                  <dd className="mt-1 leading-relaxed text-muted-foreground">
                    {t(TOPIC_BLURB_KEY[category.slug])}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mt-14">
            <h2 className="text-xl font-semibold">{t('include.title')}</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{t('include.intro')}</p>
            <ul className="mt-4 list-disc space-y-2 pl-5 leading-relaxed text-muted-foreground">
              {INCLUDE_KEYS.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </section>

          <section className="mt-14">
            <h2 className="text-xl font-semibold">{t('elsewhere.title')}</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{t('elsewhere.body')}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                href={routes.help()}
                prefetch="intent"
                className="inline-flex rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                {t('elsewhere.helpLink')}
              </Link>
              <Link
                href={routes.feedback()}
                prefetch="intent"
                className="inline-flex rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                {t('elsewhere.feedbackLink')}
              </Link>
            </div>
          </section>
        </PageContainer>
      </div>
    </>
  );
}
